import { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';

import {
  Alert,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  TextField,
} from '@mui/material';

import { useCreateProductBarcode } from '../../hooks';
import { analyzeBarcodeValue } from '../../lib/barcodeValidation';
import type { ToastSeverity } from '../../lib/useToast';
import { PRODUCT_BARCODE_TYPE_LABELS } from '../../model/options';

export interface ProductBarcodeFormDialogProps {
  open: boolean;
  productId: string;
  /**
   * Whether the product already has at least one ACTIVE barcode. The very first ACTIVE barcode a
   * product gets becomes primary automatically server-side regardless of the request's `primary`
   * flag (user-story-catalog-020 AC3), so the "Set as primary" checkbox is hidden — not merely
   * disabled — when this is `false`: it would have no effect either way, and showing it invites a
   * false "I need to check this" expectation.
   */
  hasExistingBarcodes: boolean;
  onClose: () => void;
  onResult: (severity: ToastSeverity, message: string) => void;
}

/**
 * "Add barcode" dialog (user-story-merchant-portal-039). Two submit paths:
 *  - **Standard** (the "Non-standard code" checkbox unchecked): the value must be digits of one
 *    of the four checksum-bearing GTIN lengths (8/12/13/14) with a valid GS1 mod-10 checksum,
 *    validated client-side (`analyzeBarcodeValue`) before the request is even sent — `type` is
 *    omitted so the server auto-detects it (identically to this dialog's own detection).
 *  - **Non-standard**: any non-empty value is accepted as-is, with `type: 'OTHER'` sent explicitly
 *    — no length or checksum validation, client- or server-side (matches the master service's own
 *    `OTHER`-type handling, user-story-catalog-020).
 *
 * Server 400 (checksum/validation) and 409 (duplicate-value — only possible cross-product; the
 * same value re-attached to the same product is idempotent, never a 409) errors surface inline
 * (this dialog stays open so the value can be corrected); success closes the dialog and reports a
 * toast via `onResult`, mirroring `GenerateVariantMatrixDialog`'s inline-error convention.
 */
export function ProductBarcodeFormDialog({
  open,
  productId,
  hasExistingBarcodes,
  onClose,
  onResult,
}: ProductBarcodeFormDialogProps) {
  const { t } = useTranslation('catalog');
  const create = useCreateProductBarcode();

  const [value, setValue] = useState('');
  const [nonStandard, setNonStandard] = useState(false);
  const [makePrimary, setMakePrimary] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setValue('');
    setNonStandard(false);
    setMakePrimary(false);
    setSubmitError(null);
  }, [open]);

  const analysis = analyzeBarcodeValue(value);
  const trimmed = value.trim();

  const clientError: string | null =
    trimmed === '' || nonStandard
      ? null
      : !analysis.isDigitsOnly
        ? t('barcodes.form.onlyDigits')
        : !analysis.isStandardLength
          ? t('barcodes.form.invalidLength')
          : analysis.checksumValid === false
            ? t('barcodes.form.checksumInvalid')
            : null;

  const valid = trimmed !== '' && clientError === null;
  const showDetectedTypeChip = !nonStandard && analysis.isStandardLength;

  const handleSubmit = () => {
    if (!valid) return;
    setSubmitError(null);
    create.mutate(
      {
        productId,
        body: nonStandard
          ? { value: trimmed, type: 'OTHER', primary: makePrimary || undefined }
          : { value: analysis.normalized, primary: makePrimary || undefined },
      },
      {
        onSuccess: (barcode) => {
          onResult('success', t('barcodes.addSuccess', { value: barcode.value }));
          onClose();
        },
        onError: (error) => {
          // BARCODE_DUPLICATE only fires when the value is ACTIVE on a DIFFERENT product (the
          // same product re-attaching its own value is idempotent, never an error) — a precise,
          // translated message reads better here than relaying the raw server text verbatim.
          setSubmitError(
            error.code === 'BARCODE_DUPLICATE' ? t('barcodes.form.duplicateError') : error.message,
          );
        },
      },
    );
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('barcodes.form.title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Stack spacing={1}>
            <TextField
              label={t('barcodes.form.valueLabel')}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              error={clientError !== null}
              helperText={clientError ?? t('barcodes.form.valueHelper')}
              autoFocus
              fullWidth
              slotProps={{ htmlInput: { style: { fontFamily: 'JetBrains Mono, monospace' } } }}
            />
            {showDetectedTypeChip ? (
              <Chip
                size="small"
                variant="outlined"
                color={analysis.checksumValid ? 'success' : 'error'}
                label={t('barcodes.form.detectedTypeChip', {
                  type: t(PRODUCT_BARCODE_TYPE_LABELS[analysis.detectedType]),
                })}
                sx={{ alignSelf: 'flex-start' }}
              />
            ) : null}
          </Stack>

          <FormControlLabel
            control={
              <Checkbox checked={nonStandard} onChange={(e) => setNonStandard(e.target.checked)} />
            }
            label={t('barcodes.form.nonStandardCheckbox')}
          />
          {nonStandard ? (
            <Alert severity="info" variant="outlined" sx={{ mt: -1 }}>
              {t('barcodes.form.nonStandardHelper')}
            </Alert>
          ) : null}

          {hasExistingBarcodes ? (
            <FormControlLabel
              control={
                <Checkbox checked={makePrimary} onChange={(e) => setMakePrimary(e.target.checked)} />
              }
              label={t('barcodes.setPrimaryAction')}
            />
          ) : null}

          {submitError ? <Alert severity="error">{submitError}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={create.isPending}>
          {t('common:action.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!valid || create.isPending}
          startIcon={create.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {t('common:action.add')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
